(() => {
  'use strict';

  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function announce(message) {
    const region = qs('[data-live-region]');
    if (region) region.textContent = message;
  }

  function formatMoney(cents, format) {
    const value = Number(cents || 0) / 100;
    const template = format || document.body.dataset.moneyFormat || '{{amount}} TL';
    const number = new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
    return template
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(value).toLocaleString(document.documentElement.lang || 'tr-TR'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, number)
      .replace(/\{\{\s*amount\s*\}\}/g, number)
      .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, number);
  }

  function initHeader() {
    const menuButton = qs('[data-menu-toggle]');
    const menu = qs('[data-mobile-menu]');
    const searchButton = qs('[data-search-toggle]');
    const searchPanel = qs('[data-search-panel]');

    const closeMenu = () => {
      if (!menu || !menuButton) return;
      menu.hidden = true;
      menuButton.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('menu-open');
    };
    const closeSearch = () => {
      if (!searchPanel || !searchButton) return;
      searchPanel.hidden = true;
      searchButton.setAttribute('aria-expanded', 'false');
    };

    menuButton?.addEventListener('click', () => {
      const willOpen = menu.hidden;
      closeSearch();
      menu.hidden = !willOpen;
      menuButton.setAttribute('aria-expanded', String(willOpen));
      document.body.classList.toggle('menu-open', willOpen);
    });
    searchButton?.addEventListener('click', () => {
      const willOpen = searchPanel.hidden;
      closeMenu();
      searchPanel.hidden = !willOpen;
      searchButton.setAttribute('aria-expanded', String(willOpen));
      if (willOpen) setTimeout(() => qs('input[type="search"]', searchPanel)?.focus(), 40);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeMenu();
        closeSearch();
        closeCartDrawer();
      }
    });
  }

  const drawer = () => qs('[data-cart-drawer]');
  function openCartDrawer(message) {
    const shell = drawer();
    if (!shell) return;
    shell.hidden = false;
    document.body.classList.add('drawer-open');
    if (message) {
      const content = qs('[data-drawer-content]', shell);
      if (content) content.innerHTML = `<div class="cart-drawer__empty-key" aria-hidden="true">✓</div><p>${message}</p>`;
    }
    setTimeout(() => qs('[data-drawer-close]', shell)?.focus(), 30);
  }
  function closeCartDrawer() {
    const shell = drawer();
    if (!shell || shell.hidden) return;
    shell.hidden = true;
    document.body.classList.remove('drawer-open');
  }
  function initCartDrawer() {
    qsa('[data-cart-open]').forEach((button) => button.addEventListener('click', () => openCartDrawer()));
    qsa('[data-drawer-close]').forEach((button) => button.addEventListener('click', closeCartDrawer));
  }
  async function refreshCartCount() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      qsa('[data-cart-count]').forEach((badge) => {
        badge.textContent = cart.item_count;
        badge.hidden = cart.item_count === 0;
      });
      return cart;
    } catch (_) {
      return null;
    }
  }

  function initReveal(scope = document) {
    const items = qsa('.reveal:not(.is-visible)', scope);
    if (!items.length) return;
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -35px' });
    items.forEach((item, index) => {
      item.style.transitionDelay = `${Math.min(index % 4, 3) * 70}ms`;
      observer.observe(item);
    });
  }

  function bindPointerTilt(element, xVariable, yVariable, strength = 9) {
    if (!element || reducedMotion.matches) return;
    element.addEventListener('pointermove', (event) => {
      if (event.pointerType === 'touch') return;
      const rect = element.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;
      element.style.setProperty(xVariable, `${-y * strength}deg`);
      element.style.setProperty(yVariable, `${x * strength}deg`);
    });
    element.addEventListener('pointerleave', () => {
      element.style.removeProperty(xVariable);
      element.style.removeProperty(yVariable);
    });
  }

  function initTilt(scope = document) {
    bindPointerTilt(qs('[data-hero-tilt]', scope), '--hero-rx', '--hero-ry', 16);
    qsa('[data-tilt]', scope).forEach((card) => {
      if (card.dataset.tiltBound) return;
      card.dataset.tiltBound = 'true';
      card.addEventListener('pointermove', (event) => {
        if (event.pointerType === 'touch' || window.innerWidth < 990 || reducedMotion.matches) return;
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        card.style.transform = `perspective(1100px) rotateX(${-y * 5}deg) rotateY(${x * 6}deg) translateY(-2px)`;
      });
      card.addEventListener('pointerleave', () => { card.style.transform = ''; });
    });
  }

  function setPressed(buttons, active) {
    buttons.forEach((button) => {
      const selected = button === active;
      button.classList.toggle('is-active', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
  }

  function initStudio(scope = document) {
    qsa('[data-keycap-studio]', scope).forEach((studio) => {
      if (studio.dataset.studioBound) return;
      studio.dataset.studioBound = 'true';
      const stage = qs('[data-studio-stage]', studio);
      bindPointerTilt(stage, '--studio-rx', '--studio-ry', 14);

      const layoutButtons = qsa('[data-layout]', studio);
      layoutButtons.forEach((button) => button.addEventListener('click', () => {
        studio.dataset.currentLayout = button.dataset.layout;
        setPressed(layoutButtons, button);
        announce(`${button.textContent.trim()} klavye düzeni seçildi`);
      }));

      const keyColorButtons = qsa('[data-key-color]', studio);
      keyColorButtons.forEach((button) => button.addEventListener('click', () => {
        studio.style.setProperty('--studio-key', button.dataset.keyColor);
        setPressed(keyColorButtons, button);
      }));

      const accentButtons = qsa('[data-accent-color]', studio);
      accentButtons.forEach((button) => button.addEventListener('click', () => {
        const color = button.dataset.accentColor;
        studio.classList.toggle('is-rainbow', color === 'rainbow');
        if (color !== 'rainbow') studio.style.setProperty('--studio-accent', color);
        setPressed(accentButtons, button);
      }));

      const keys = qsa('[data-key]', studio);
      const selectedLabel = qs('[data-selected-key]', studio);
      const selectKey = (key) => {
        keys.forEach((item) => item.classList.toggle('is-selected', item === key));
        key.classList.add('is-pressed');
        setTimeout(() => key.classList.remove('is-pressed'), 130);
        if (selectedLabel) selectedLabel.textContent = key.dataset.key || 'KEY';
        announce(`${key.dataset.key} tuşu seçildi`);
      };
      keys.forEach((key) => key.addEventListener('click', () => selectKey(key)));
      window.addEventListener('keydown', (event) => {
        if (/^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName)) return;
        const match = keys.find((key) => key.dataset.key.toLowerCase() === event.key.toLowerCase());
        if (match) selectKey(match);
      });
    });
  }

  function initGallery(scope = document) {
    qsa('[data-product-gallery]', scope).forEach((gallery) => {
      qsa('[data-media-target]', gallery).forEach((button) => button.addEventListener('click', () => {
        qsa('[data-media-id]', gallery).forEach((media) => media.classList.toggle('is-hidden', media.dataset.mediaId !== button.dataset.mediaTarget));
        qsa('[data-media-target]', gallery).forEach((thumb) => thumb.classList.toggle('is-active', thumb === button));
      }));
    });
  }

  function initQuantity(scope = document) {
    qsa('[data-quantity], .quantity-stepper', scope).forEach((stepper) => {
      if (stepper.dataset.quantityBound) return;
      stepper.dataset.quantityBound = 'true';
      const input = qs('input[type="number"]', stepper);
      qs('[data-quantity-minus]', stepper)?.addEventListener('click', () => {
        const min = Number(input.min || 0);
        input.value = Math.max(min, Number(input.value || 0) - 1);
      });
      qs('[data-quantity-plus]', stepper)?.addEventListener('click', () => {
        const max = Number(input.max || Infinity);
        input.value = Math.min(max, Number(input.value || 0) + 1);
      });
    });
  }

  function initProduct(scope = document) {
    qsa('[data-product-root]', scope).forEach((root) => {
      const form = qs('[data-product-form]', root);
      if (!form || form.dataset.productBound) return;
      form.dataset.productBound = 'true';
      const variantData = qs('[data-product-variants]', form);
      const variants = variantData ? JSON.parse(variantData.textContent) : [];
      const optionSelects = qsa('[data-option-select]', form);
      const variantInput = qs('[data-variant-id]', form);
      const submit = qs('[data-add-to-cart]', form);
      const label = qs('[data-add-label]', submit);
      const addPrice = qs('[data-add-price]', submit);
      const mainPrice = qs('[data-product-price]', root);
      const message = qs('[data-product-message]', form);

      const updateVariant = () => {
        const selected = optionSelects.map((select) => select.value);
        const variant = variants.find((item) => item.options.every((value, index) => value === selected[index])) || variants[0];
        if (!variant) return;
        variantInput.value = variant.id;
        submit.disabled = !variant.available;
        label.textContent = variant.available ? 'Sepete ekle' : 'Tükendi';
        addPrice.textContent = formatMoney(variant.price);
        if (mainPrice) {
          const compare = variant.compare_at_price && variant.compare_at_price > variant.price ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` : '';
          mainPrice.innerHTML = `<div class="price"><span class="price__current">${formatMoney(variant.price)}</span>${compare}</div>`;
        }
        if (variant.featured_media?.id) qs(`[data-media-target="${variant.featured_media.id}"]`, root)?.click();
        if (history.replaceState) history.replaceState({}, '', `${location.pathname}?variant=${variant.id}`);
      };
      optionSelects.forEach((select) => select.addEventListener('change', updateVariant));

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (submit.disabled) return;
        submit.disabled = true;
        label.textContent = 'Ekleniyor…';
        message.hidden = true;
        try {
          const endpoint = `${form.action.replace(/\/$/, '')}.js`;
          const response = await fetch(endpoint, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error((await response.json()).description || 'Ürün eklenemedi.');
          await refreshCartCount();
          label.textContent = 'Sepete eklendi ✓';
          message.textContent = 'Harika seçim — ürün sepetinde.';
          message.classList.remove('is-error');
          message.hidden = false;
          announce('Ürün sepete eklendi');
          openCartDrawer('Ürün sepete eklendi. Seçimini tamamlamaya hazırsın.');
          setTimeout(() => { label.textContent = 'Sepete ekle'; submit.disabled = false; }, 1600);
        } catch (error) {
          message.textContent = error.message || 'Bir sorun oluştu. Lütfen tekrar dene.';
          message.classList.add('is-error');
          message.hidden = false;
          label.textContent = 'Tekrar dene';
          submit.disabled = false;
        }
      });
    });
  }

  function initCollection(scope = document) {
    qs('[data-sort-select]', scope)?.addEventListener('change', (event) => event.target.form.submit());
    const filterToggle = qs('[data-filter-toggle]', scope);
    const filterPanel = qs('[data-filter-panel]', scope);
    filterToggle?.addEventListener('click', () => {
      const open = filterPanel.classList.toggle('is-open');
      filterToggle.setAttribute('aria-expanded', String(open));
    });
  }

  function init(scope = document) {
    initReveal(scope);
    initTilt(scope);
    initStudio(scope);
    initGallery(scope);
    initQuantity(scope);
    initProduct(scope);
    initCollection(scope);
  }

  document.addEventListener('DOMContentLoaded', () => {
    initHeader();
    initCartDrawer();
    init(document);
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
